import {
  AVATAR_BASE_HEIGHT,
  BREAST_ARM_PITCH_KEYS,
  BREAST_ARM_SPREAD_KEYS,
  BREAST_BREATH_HEAD_RAD,
  BREAST_BREATH_KEYS,
  BREAST_BREATH_LIFT,
  BREAST_BREATH_NECK_RAD,
  BREAST_BREATH_PITCH_RAD,
  BREAST_CYCLE_SECONDS,
  BREAST_ELBOW_KEYS,
  BREAST_HIP_KEYS,
  BREAST_KNEE_KEYS,
  BREAST_LEG_SPREAD_KEYS,
  BREAST_SURGE_KEYS,
  BREAST_SURGE_STUDS,
  DASH_AMP_LERP,
  DEATH_POSE_LERP,
  JUMP_POSE_LERP,
  JUMP_VERTICAL_VELOCITY_THRESHOLD,
  RUN_SLOPE_AIRBORNE_IGNORE_DURATION,
  RUN_SLOPE_AIRBORNE_MIN_STRIDE,
  LANDING_CROUCH_DIP,
  LANDING_CROUCH_DURATION,
  POSE_ASCENT_LOWER_LEG,
  POSE_ASCENT_UPPER_LEG,
  POSE_DESCENT_LOWER_LEG,
  POSE_DESCENT_UPPER_LEG,
  POSE_JUMP_LOWER_ARM,
  POSE_JUMP_UPPER_ARM,
  POSE_LANDING_LOWER_ARM,
  POSE_LANDING_LOWER_LEG,
  POSE_LANDING_UPPER_ARM,
  POSE_LANDING_UPPER_LEG,
  POSE_PREP_LOWER_LEG,
  POSE_PREP_UPPER_LEG,
  POSE_TAKEOFF_LOWER_LEG,
  POSE_TAKEOFF_UPPER_LEG,
  SIT_ELBOW_BEND_RAD,
  SIT_HIP_FLEX_RAD,
  SIT_KNEE_BEND_RAD,
  SIT_POSE_LERP,
  SIT_UPPER_ARM_PITCH_RAD,
  SLIDE_LEAN_RAD,
  SLIDE_POSE_LERP,
  STRIDE_AMP_RAMP_IN,
  STRIDE_AMP_RAMP_OUT,
  STRIDE_MOVE_THRESHOLD,
  SWIM_BREAST_LEAN_RAD,
  SWIM_FLOAT_ARM_RAD,
  SWIM_FLOAT_ARM_SPREAD_RAD,
  SWIM_FLOAT_BOB_AMPLITUDE,
  SWIM_FLOAT_LEAN_RAD,
  SWIM_POSE_LERP,
  SWIM_SURFACE_VISUAL_LIFT,
  SWIM_SURFACE_VISUAL_LIFT_MOVE_EXTRA,
  TAKEOFF_IMPULSE_DURATION,
  VERTICAL_VELOCITY_REFERENCE,
  sampleSwimKeyframes,
} from './avatarPoseConstants'
import { runMotionParams } from './runMotionParams'
import { REST_POSE_UPPER_ARM_Z, VrmAvatarEntry } from './vrmAvatarEntry'

import { AvatarMotionState } from '../../types'

/**
 * 移動・ジャンプアニメーションと VRM のフレーム更新。
 *
 * - 水平: XZ 距離から距離駆動の位相を進め、上腕/上脚を逆位相 sin で振る。
 * - 垂直: 物理側 motion state（grounded / verticalVelocity）が利用できれば
 *   そちらを優先し、未注入時は位置 Y 差分から推定する。
 *   - Space → 即離陸して空中（jumpPoseAmp 1.0、tuck pose）。予備動作（屈伸）は無い
 *   - 着地 → 屈伸沈み込み（landingCrouchRemaining）の余韻を残す
 * - 死亡演出: deathKind='health' は倒れポーズ、'void' はアバター非表示。
 * - VRM は spring bones / expressions のため毎フレーム vrm.update(dt) を呼ぶ。
 */
export const animateVrmAvatar = (
  entry: VrmAvatarEntry,
  dt: number,
  motionState: AvatarMotionState | null,
): void => {
  if (dt <= 0) {
    return
  }
  const scaleH = entry.scaleHKey ?? 1
  const x = entry.group.position.x
  const z = entry.group.position.z
  const dx = x - entry.prevX
  const dz = z - entry.prevZ
  entry.prevX = x
  entry.prevZ = z
  const distanceStep = Math.sqrt(dx * dx + dz * dz)
  const speed = distanceStep / dt
  const moving = speed > STRIDE_MOVE_THRESHOLD

  // 滑走（すべり台）中は高速で位置が動くが走りではないため、走り系の
  // エンベロープを育てない（滑走解除時に蓄積済みの走りモーションが漏れない）。
  // 乗車（isSeated）中も同様: 車両が kinematic にアバターを運ぶため、XZ 速度
  // から走りが発火すると座ったまま脚が走ってしまう。
  const sliding = motionState?.isSliding === true
  const seated = motionState?.isSeated === true
  if (moving && !sliding && !seated) {
    // 距離駆動: 1 サイクル = アバター身長 * STRIDE_CYCLE_DISTANCE_RATIO の距離
    // 進むごとに位相が 2π 進む。これで速度と歩幅が物理的に同期する。
    const cycleDistance =
      AVATAR_BASE_HEIGHT * scaleH * runMotionParams.cycleDistanceRatio
    entry.runTime += (distanceStep / cycleDistance) * Math.PI * 2
    entry.strideAmplitude += (1 - entry.strideAmplitude) * STRIDE_AMP_RAMP_IN
  } else {
    entry.strideAmplitude += (0 - entry.strideAmplitude) * STRIDE_AMP_RAMP_OUT
  }

  // 走行エンベロープ: 歩行（移動中）は常に走り相当のモーション（前傾・大きな
  // 腕振り・腿上げ）を使う。以前は Shift ダッシュ時のみだったが、通常の歩きも
  // この走りモーションへ統一した（速度は歩行のまま）。on/off を直に使うと
  // 切替が硬いので指数 lerp で繋ぐ。
  const dashTarget = moving && !sliding && !seated ? 1 : 0
  entry.dashAmplitude += (dashTarget - entry.dashAmplitude) * DASH_AMP_LERP

  // --- 遊泳エンベロープとストローク位相 ---
  // 遊泳中は歩行/走行/ジャンプの各ポーズを swimAmp で抑制し、前傾 + 平泳ぎ/
  // 浮遊の泳ぎポーズへクロスフェードする。死亡演出中は泳ぎ前傾や着席ポーズが
  // 倒れ込みに混ざらないよう即座に 0 へ落とす（死亡演出が最優先）。
  if ((motionState?.deathKind ?? null) !== null) {
    entry.swimPoseAmp = 0
    entry.slidePoseAmp = 0
    entry.sitPoseAmp = 0
  }
  const swimTarget = motionState?.isSwimming ? 1 : 0
  entry.swimPoseAmp += (swimTarget - entry.swimPoseAmp) * SWIM_POSE_LERP
  const swimAmp = entry.swimPoseAmp
  // すべり台滑走（仰向けで足から滑るポーズ）へのクロスフェード。
  const slideTarget = motionState?.isSliding ? 1 : 0
  entry.slidePoseAmp += (slideTarget - entry.slidePoseAmp) * SLIDE_POSE_LERP
  const slideAmp = entry.slidePoseAmp
  // 着席（vehicle 乗車）ポーズへのクロスフェード。
  const sitTarget = seated ? 1 : 0
  entry.sitPoseAmp += (sitTarget - entry.sitPoseAmp) * SIT_POSE_LERP
  const sitAmp = entry.sitPoseAmp
  // 乗車中はジャンプ/着地系のポーズ・バウンスを源流から抑制するマスク
  //（1 = 通常、0 = 完全着席）。車両が段差で跳ねても座位を崩さない。
  const sitMask = 1 - sitAmp
  // 平泳ぎ ⇔ 浮遊は「移動キー入力の有無」（isSwimmingMoving）で切り替える。
  // 描画位置の速度由来（dashAmplitude）だと、岸や壁に押されて進めない間に
  // 泳ぎが止まって見える。
  const swimmingMoving = motionState?.isSwimmingMoving === true
  const swimMoveTarget = swimmingMoving ? 1 : 0
  // 泳ぎ始め（移動入力の立ち上がり）はサイクル先頭（グライド）から開始する。
  // ブレンド量ではなく入力の rising edge で判定し、入力の素早い離し直しでも
  // 仕様どおりグライドから入る。
  if (swimmingMoving && !entry.wasSwimmingMoving) {
    entry.swimTime = 0
  }
  entry.wasSwimmingMoving = swimmingMoving
  entry.swimMoveAmp += (swimMoveTarget - entry.swimMoveAmp) * DASH_AMP_LERP
  if (swimAmp > 0.01) {
    // 長時間泳いでも位相精度が落ちないよう、サイクル長で丸めながら進める。
    entry.swimTime = (entry.swimTime + dt) % BREAST_CYCLE_SECONDS
  }
  // 平泳ぎサイクルの正規化位相（0..1）と各チャンネルのサンプル値。
  const breastPhase =
    (entry.swimTime % BREAST_CYCLE_SECONDS) / BREAST_CYCLE_SECONDS
  const breastBreath = sampleSwimKeyframes(BREAST_BREATH_KEYS, breastPhase)
  const breastSurge = sampleSwimKeyframes(BREAST_SURGE_KEYS, breastPhase)

  // --- 垂直方向の状態（屈伸 / 空中 / 着地）---
  const y = entry.group.position.y
  const vy = motionState ? motionState.verticalVelocity : (y - entry.prevY) / dt
  entry.prevY = y

  // 物理側 motion state が利用可能ならそれを正とする。fallback として位置 Y 差分推定。
  // ジャンプの予備動作（屈伸）は廃止したため prepAmp は常に 0（即離陸）。
  const prepAmp = 0
  let isAirborne: boolean
  if (motionState) {
    const rawAirborne = !motionState.grounded
    // 走行中（ストライドが立っている）に下り坂・段差で grounded が 1〜数フレームだけ
    // チラついても、走行モーションが両脚揃えの空中ポーズへ飛ばないよう、上昇でない
    // 短時間の非接地は接地として吸収する。接地フレームで猶予をリチャージし、猶予を
    // 超えて続く非接地（本物の落下・ジャンプ滞空）や上昇（ジャンプ離陸 = vy がしきい値
    // 以上）は通常どおり空中とする。物理側の grounded（接触・ジャンプ可否）はこの吸収の
    // 影響を受けない（ここは描画モーションの判定のみ）。
    if (
      rawAirborne &&
      vy < JUMP_VERTICAL_VELOCITY_THRESHOLD &&
      entry.strideAmplitude > RUN_SLOPE_AIRBORNE_MIN_STRIDE &&
      entry.airborneIgnoreRemaining > 0
    ) {
      isAirborne = false
      entry.airborneIgnoreRemaining = Math.max(
        0,
        entry.airborneIgnoreRemaining - dt,
      )
    } else {
      isAirborne = rawAirborne
      // 吸収しない非接地フレーム（落下開始・上昇=ジャンプ離陸・走行が未だ立ち上がって
      // いない・猶予切れ）では猶予を即座に失効させる。接地直後にリチャージした猶予が
      // 落下中に温存されると、数フレーム後に水平移動で走行が立ち上がった瞬間や走行
      // ジャンプの頂点（vy が 0 付近へ戻る）で吸収が誤発火し、空中で地上走行ポーズへ
      // スナップしてしまう。非接地に入った時点で使い切ることでこれを防ぐ。
      if (rawAirborne) {
        entry.airborneIgnoreRemaining = 0
      }
    }
    if (!rawAirborne) {
      entry.airborneIgnoreRemaining = RUN_SLOPE_AIRBORNE_IGNORE_DURATION
    }
  } else {
    if (
      entry.groundedY === null &&
      Math.abs(vy) < JUMP_VERTICAL_VELOCITY_THRESHOLD
    ) {
      entry.groundedY = y
    }
    isAirborne =
      Math.abs(vy) > JUMP_VERTICAL_VELOCITY_THRESHOLD ||
      (entry.groundedY !== null && y > entry.groundedY + 0.2)
  }

  // リスポーン/リセットの spawn テレポート（respawnGeneration 変化）を検出する。
  // 落下中(空中)→ spawn(接地) は本物の着地ではないため、屈伸沈み込み（足が spawn 台へ
  // めり込む原因）を発動させない。物理が 1 フレームに複数ステップ進んでも、世代の
  // 差分判定なら取りこぼさない。
  const respawned =
    motionState !== null &&
    motionState.respawnGeneration !== entry.lastRespawnGeneration
  if (motionState !== null) {
    entry.lastRespawnGeneration = motionState.respawnGeneration
  }

  // --- 死亡演出（Roblox の RespawnTime 相当、3 秒間） ---
  const deathKind = motionState?.deathKind ?? null
  if (deathKind !== entry.lastDeathKind) {
    // 'void'（奈落落下）はアバターを消す。カメラの注視点（group.position）は
    // 物理側が更新を止めるため、消えた場所に留まる。生存復帰で再表示する。
    entry.group.visible = deathKind !== 'void'
    entry.lastDeathKind = deathKind
  }
  if (respawned) {
    // リスポーンは新しい体での復活。倒れポーズは持ち越さない。
    entry.deathPoseAmp = 0
  }
  // 体力 0（'health'）は倒れ込みポーズへ指数 lerp で遷移する。倒れる向きと
  // 最大倒れ角は物理側が障害物の空きから決める（rotation.x 負=前傾なので
  // forward は負符号。狭い場所では 90° 未満で止めて貫通を防ぐ）。
  const deathPoseTarget = deathKind === 'health' ? 1 : 0
  entry.deathPoseAmp += (deathPoseTarget - entry.deathPoseAmp) * DEATH_POSE_LERP
  const deathFall = motionState?.deathFall ?? null
  if (deathFall) {
    entry.deathFallSign = deathFall.direction === 'backward' ? 1 : -1
    entry.deathFallMaxAngleRad = deathFall.maxAngleRad
  }

  const wasAirborne = entry.airborne
  entry.airborne = isAirborne
  // airborne → grounded 遷移で着地屈伸を開始（接地基準 Y を現在 Y で更新）。
  // ただしテレポート復帰フレームは誤着地なので屈伸させず、進行中の屈伸も解除する。
  if (respawned) {
    entry.landingCrouchRemaining = 0
    entry.groundedY = y
  } else if (wasAirborne && !isAirborne) {
    // 入水（遊泳開始）は着地ではないため屈伸させない。
    if (!motionState?.isSwimming) {
      entry.landingCrouchRemaining = LANDING_CROUCH_DURATION
    }
    entry.groundedY = y
  }
  if (entry.landingCrouchRemaining > 0) {
    entry.landingCrouchRemaining = Math.max(
      0,
      entry.landingCrouchRemaining - dt,
    )
  }

  // テイクオフ衝撃: 離陸フレーム（接地 → 空中遷移で上向き初速あり）に
  // 短い跳躍ピーク（脚を伸ばし切る）を発動する。テレポート復帰では発動しない。
  if (!respawned && !wasAirborne && isAirborne && vy > 1) {
    entry.takeoffImpulseRemaining = TAKEOFF_IMPULSE_DURATION
  }
  if (entry.takeoffImpulseRemaining > 0) {
    entry.takeoffImpulseRemaining = Math.max(
      0,
      entry.takeoffImpulseRemaining - dt,
    )
  }

  // 各フェーズの 0..1 重み。屈伸 / テイクオフ / 上昇 / 下降 / 着地は順に発火し、
  // 多少の重なりを持って指数 lerp で繋がるよう設計してある。
  // 乗車中（sitMask）はいずれも抑制する — 車両の上下動で空中 tuck や
  // 着地屈伸が出ると座位が崩れるため、源流の重みごとマスクする。
  const airborneSmoothed =
    (() => {
      const target = isAirborne ? 1 : 0
      entry.jumpPoseAmp += (target - entry.jumpPoseAmp) * JUMP_POSE_LERP
      return entry.jumpPoseAmp
    })() * sitMask
  const takeoffAmp =
    (entry.takeoffImpulseRemaining / TAKEOFF_IMPULSE_DURATION) * sitMask
  // 鉛直速度を -1..1 に正規化し、正側=上昇、負側=下降に振り分け。
  const vyNorm = Math.max(-1, Math.min(1, vy / VERTICAL_VELOCITY_REFERENCE))
  const ascendingAmp = airborneSmoothed * (1 - takeoffAmp) * Math.max(0, vyNorm)
  const descendingAmp =
    airborneSmoothed * (1 - takeoffAmp) * Math.max(0, -vyNorm)
  const landingAmp =
    (entry.landingCrouchRemaining / LANDING_CROUCH_DURATION) * sitMask

  // 地上の屈み（離陸前 + 着地）は body sink にも使うのでまとめて持つ。
  const groundCrouchAmp = Math.max(prepAmp, landingAmp)

  // 走り振り幅は屈伸・空中時にはほぼ消す（歩行/走行と排他にする）。
  // 滑走中・乗車中は位置が動いていても走りモーションを出さない
  //（滑走は仰向けで滑り、乗車は車両が運ぶため）。
  const groundAmp =
    entry.strideAmplitude *
    (1 - airborneSmoothed) *
    (1 - groundCrouchAmp) *
    (1 - slideAmp) *
    sitMask

  // ジャンプ中（離陸 takeoff / 空中 airborne / 着地 landing のいずれか）は走りの
  // 左右非対称な動き（腕脚スイング・腿上げ・膝曲げ・前傾）を完全に止め、本家準拠の
  // 左右対称な両手上げジャンプを保つ。groundAmp の (1 - airborneSmoothed) だけだと
  // 離陸直後の数フレームに走りが残り、走行ジャンプの出だしが非対称に見えるため、
  // takeoff/landing も含めて抑える。
  const jumpPoseMask = Math.min(1, takeoffAmp + airborneSmoothed + landingAmp)
  const runSwingGate = 1 - jumpPoseMask

  // ダッシュ増幅: 走行スイング/バウンスを最大 (1 + BOOST) 倍に。前傾は地上走行中のみ。
  const strideBoost =
    1 + runMotionParams.dashStrideAmpBoost * entry.dashAmplitude
  // dashGate に runSwingGate を掛け、ジャンプ中は腿上げ・膝曲げ・前傾を止める。
  const dashGate = entry.dashAmplitude * groundAmp * runSwingGate

  // 縦バウンス: 通常走り bob + 屈伸/着地時の沈み込み + 遊泳中の浮き沈み。
  // 走り bob・着地沈み込みは地上専用なので遊泳中は (1 - swimAmp) で抑制する。
  const phase = Math.sin(entry.runTime)
  // 走りの上下動は1歩ごと（1サイクルに2回）弾む。脚が体の真下を通る局面で沈み、
  // 蹴り出し〜遊脚で持ち上がる二重バウンスにして、sin1周期ぶんの単調な横揺れを避ける。
  const runBounce = -Math.cos(entry.runTime * 2)
  const groundVisualMask = (1 - swimAmp) * (1 - slideAmp)
  const swimMoveBlend = entry.swimMoveAmp
  // 浮遊はゆっくりした上下動、平泳ぎは呼吸（胸上げ）に同期した上下動。
  const swimBob =
    Math.sin(entry.swimTime * 2) *
      SWIM_FLOAT_BOB_AMPLITUDE *
      (1 - swimMoveBlend) *
      swimAmp +
    breastBreath * BREAST_BREATH_LIFT * swimMoveBlend * swimAmp
  entry.bobOffset =
    runBounce *
      runMotionParams.bobAmplitude *
      scaleH *
      groundAmp *
      strideBoost *
      groundVisualMask *
      runSwingGate +
    swimBob
  const crouchDip =
    -LANDING_CROUCH_DIP * scaleH * groundCrouchAmp * groundVisualMask
  // 遊泳の前傾はモデル原点（足元）まわりの回転なので、そのままだと頭が
  // 前方下へ沈む。身体の中心が回転前と同じ位置に留まるよう、回転で動く
  // 中心点ぶんを並進（Y 持ち上げ + Z 補正）で打ち消す。
  // どちらもうつ伏せ（負 = 前傾）。平泳ぎ中は呼吸（インスイープ）で胸が
  // 上がるぶんだけ前傾を戻す — 頭だけを反らせず、胸から上がって口が水面に
  // 出る見え方にする。
  const breastGate = swimMoveBlend * swimAmp
  const swimLeanRad =
    -(
      SWIM_FLOAT_LEAN_RAD +
      (SWIM_BREAST_LEAN_RAD - SWIM_FLOAT_LEAN_RAD) * swimMoveBlend
    ) *
      swimAmp +
    BREAST_BREATH_PITCH_RAD * breastBreath * breastGate
  // 滑走は後傾（正）。遊泳（前傾、負）とは排他に発生する想定で単純加算する。
  const slideLeanRad = SLIDE_LEAN_RAD * slideAmp
  const poseLeanRad = swimLeanRad + slideLeanRad
  const halfHeight = (AVATAR_BASE_HEIGHT * scaleH) / 2
  const swimLiftY =
    halfHeight * (1 - Math.cos(poseLeanRad)) +
    (SWIM_SURFACE_VISUAL_LIFT +
      SWIM_SURFACE_VISUAL_LIFT_MOVE_EXTRA * swimMoveBlend) *
      swimAmp
  // 前方サージ: キック直後に最も速く、腕のかき・脚の引き付けで減速して
  // 見えるよう、見た目の Z（モデル前方 = -Z）を小さく揺らす。
  const swimShiftZ =
    -halfHeight * Math.sin(poseLeanRad) -
    BREAST_SURGE_STUDS * breastSurge * breastGate
  entry.modelRoot.position.y =
    entry.modelRestY + entry.bobOffset + crouchDip + swimLiftY
  entry.modelRoot.position.z = swimShiftZ
  entry.placeholderMesh.position.y = entry.bobOffset + crouchDip
  // ダッシュ時は上体を前傾させてスプリント姿勢にする（地上走行中のみ）。
  // モデルの前方は -Z。rotation.x 正は頭が +Z（後傾）になるため、前傾は負符号。
  // 遊泳中は泳ぎの前傾（立ち泳ぎ〜ほぼ水平のクロール姿勢）が走り前傾に代わって入る。
  // 死亡（体力 0）時は forward=うつ伏せ / backward=仰向け方向へ最大
  // deathFallMaxAngleRad（十分空いていれば 90°）まで倒し込む。走り・遊泳の前傾は
  // 倒れの進行に合わせて打ち消し、物理側が決めた最大倒れ角を超えないようにする。
  entry.modelRoot.rotation.x =
    (-runMotionParams.leanRad * dashGate * (1 - swimAmp) + poseLeanRad) *
      (1 - entry.deathPoseAmp) +
    entry.deathFallSign * entry.deathFallMaxAngleRad * entry.deathPoseAmp

  // 走りスイング（地上のみ）。下腕は左右共通の肘曲げ baseline で歩行/走行を表現する。
  // ダッシュ中は腕脚の振り幅を strideBoost で増幅し、躍動感を出す。runSwingGate で
  // ジャンプ中は走りの非対称スイングと肘曲げ baseline を完全に止める。
  const armStridePitch =
    phase * runMotionParams.armPitchAmp * groundAmp * strideBoost * runSwingGate
  const legStridePitch =
    -phase *
    runMotionParams.legPitchAmp *
    groundAmp *
    strideBoost *
    runSwingGate
  const lowerArmStrideBend =
    runMotionParams.lowerArmBend * groundAmp * runSwingGate
  // ダッシュ時のランニングフォーム（地上走行中のみ）。前方へ振り出す脚で腿を高く
  // 上げ(thigh lift)、その脚の膝を深く畳む(knee tuck)。左右は phase 符号で交互。
  // 左脚は phase<0 で前方(上脚 rotation.x = -phase*amp が正)、右脚は phase>0 で前方。
  const leftFwd = Math.max(0, -phase)
  const rightFwd = Math.max(0, phase)
  // 腕の斜め前クロス: 前方へ振り出す腕を体の前（内側）へ寄せる。左腕は phase>0
  // （= rightFwd）で前方、右腕は phase<0（= leftFwd）で前方。前後だけの単調な振りを避ける。
  // (1 - swimAmp) で遊泳中は抑制する（rotation.z へ直接加算するため、走り腕振りの
  // 斜め前クロスが泳ぎの腕開きに混ざらないようにする）。
  const armCross =
    runMotionParams.armCross *
    groundAmp *
    strideBoost *
    (1 - swimAmp) *
    runSwingGate
  const leftArmCross = rightFwd * armCross
  const rightArmCross = leftFwd * armCross
  const dashThigh = runMotionParams.thighLift * dashGate
  const leftThighLift = leftFwd * dashThigh
  const rightThighLift = rightFwd * dashThigh
  // 膝曲げ: 前方スイングで最大、後方でも DASH_KNEE_BASE_FRAC だけ残して伸び切らせない。
  const dashKnee = runMotionParams.kneeBend * dashGate
  const kneeBaseFrac = runMotionParams.kneeBaseFrac
  const leftKneeBend = -dashKnee * (kneeBaseFrac + (1 - kneeBaseFrac) * leftFwd)
  const rightKneeBend =
    -dashKnee * (kneeBaseFrac + (1 - kneeBaseFrac) * rightFwd)

  // ジャンプの腕は本家準拠で左右対称に「両手を上げる」。空中（airborne）の間は
  // POSE_JUMP で頭上へ保持し（上昇・頂点・下降を通じて落ちない）、着地で脇へ
  // 下ろす。立位・走行を問わず同じフォーム。走りスイングは runSwingGate で空中は
  // 0 になるため、ここは airborneSmoothed をそのまま上げ量に使ってよい。
  const jumpUpperArm =
    POSE_JUMP_UPPER_ARM * airborneSmoothed + POSE_LANDING_UPPER_ARM * landingAmp
  const jumpLowerArm =
    POSE_JUMP_LOWER_ARM * airborneSmoothed + POSE_LANDING_LOWER_ARM * landingAmp
  const standUpperLeg =
    POSE_PREP_UPPER_LEG * prepAmp +
    POSE_TAKEOFF_UPPER_LEG * takeoffAmp +
    POSE_ASCENT_UPPER_LEG * ascendingAmp +
    POSE_DESCENT_UPPER_LEG * descendingAmp +
    POSE_LANDING_UPPER_LEG * landingAmp
  const standLowerLeg =
    POSE_PREP_LOWER_LEG * prepAmp +
    POSE_TAKEOFF_LOWER_LEG * takeoffAmp +
    POSE_ASCENT_LOWER_LEG * ascendingAmp +
    POSE_DESCENT_LOWER_LEG * descendingAmp +
    POSE_LANDING_LOWER_LEG * landingAmp

  // 遊泳ポーズ: 移動中は平泳ぎ（キーフレーム駆動の周期運動。左右対称で
  // 両腕・両脚が同時に動き、腕→呼吸→リカバリー→脚→キック→グライドの順）。
  // 停止中は浮遊（腕を軽く開いて脱力）。地上ポーズとは swimAmp でクロスフェード。
  const breastArmPitch = sampleSwimKeyframes(BREAST_ARM_PITCH_KEYS, breastPhase)
  const breastArmSpread = sampleSwimKeyframes(
    BREAST_ARM_SPREAD_KEYS,
    breastPhase,
  )
  const breastElbow = sampleSwimKeyframes(BREAST_ELBOW_KEYS, breastPhase)
  const breastHip = sampleSwimKeyframes(BREAST_HIP_KEYS, breastPhase)
  const breastKnee = sampleSwimKeyframes(BREAST_KNEE_KEYS, breastPhase)
  // 腕: 左右同値（対称のかき）。浮遊では前方へ軽く掲げた静止角へ。
  const swimArm =
    (breastArmPitch * swimMoveBlend +
      SWIM_FLOAT_ARM_RAD * (1 - swimMoveBlend)) *
    swimAmp
  // 腕の外開き（上腕 rotation.z）。安静ポーズ（REST_POSE_UPPER_ARM_Z）からの
  // 外側デルタとして与える。浮遊では軽く開いたまま。
  const swimArmSpread =
    (breastArmSpread * swimMoveBlend +
      SWIM_FLOAT_ARM_SPREAD_RAD * (1 - swimMoveBlend)) *
    swimAmp
  const swimElbow = breastElbow * breastGate
  // 脚: 引き付け（外開き）→ ウィップキック（外後方→内側へ挟む）→ 閉じて伸ばす。
  // 両脚同時（交互にしない）。
  const swimLegTuck = breastHip * breastGate
  const swimKneeBend = breastKnee * breastGate
  const swimLegSpread =
    sampleSwimKeyframes(BREAST_LEG_SPREAD_KEYS, breastPhase) * breastGate
  const groundPoseMask = 1 - swimAmp

  // 着席ポーズ: 腿を前へ上げ膝を曲げて座り、腕は軽く前へ出してハンドルへ
  // 手を添える。上体は直立のまま。走り/ジャンプ系は sitMask で源流から
  // 抑制済みなので、ここでは sitAmp での目標角の加算ブレンドのみでよい。
  const sitHipFlex = SIT_HIP_FLEX_RAD * sitAmp
  const sitKneeBend = SIT_KNEE_BEND_RAD * sitAmp
  const sitArmPitch = SIT_UPPER_ARM_PITCH_RAD * sitAmp
  const sitElbowBend = SIT_ELBOW_BEND_RAD * sitAmp

  if (entry.leftUpperArm) {
    entry.leftUpperArm.rotation.x =
      (jumpUpperArm + armStridePitch) * groundPoseMask + swimArm + sitArmPitch
    entry.leftUpperArm.rotation.z =
      REST_POSE_UPPER_ARM_Z - swimArmSpread + leftArmCross
  }
  if (entry.rightUpperArm) {
    entry.rightUpperArm.rotation.x =
      (jumpUpperArm - armStridePitch) * groundPoseMask + swimArm + sitArmPitch
    entry.rightUpperArm.rotation.z =
      -(REST_POSE_UPPER_ARM_Z - swimArmSpread) - rightArmCross
  }
  // 下腕の肘曲げ axis 検証（実機ログから X/Z は前方曲げに対応しなかった）。Y 軸を
  // 試す。本 VRM の lower arm bone は Y が前腕の長軸でない可能性があり、その場合
  // Y 軸回転が肘 hinge に対応する。
  const elbowBendValue =
    (jumpLowerArm + lowerArmStrideBend) * groundPoseMask +
    swimElbow +
    sitElbowBend
  if (entry.leftLowerArm) {
    entry.leftLowerArm.rotation.x = 0
    entry.leftLowerArm.rotation.z = 0
    entry.leftLowerArm.rotation.y = -elbowBendValue
  }
  if (entry.rightLowerArm) {
    entry.rightLowerArm.rotation.x = 0
    entry.rightLowerArm.rotation.z = 0
    entry.rightLowerArm.rotation.y = elbowBendValue
  }
  if (entry.leftUpperLeg) {
    entry.leftUpperLeg.rotation.x =
      (standUpperLeg + legStridePitch + leftThighLift) * groundPoseMask +
      swimLegTuck +
      sitHipFlex
    // ウィップキックの外開き（左脚は -z が外側）。地上ポーズでは 0 に戻る。
    entry.leftUpperLeg.rotation.z = -swimLegSpread
  }
  if (entry.rightUpperLeg) {
    entry.rightUpperLeg.rotation.x =
      (standUpperLeg - legStridePitch + rightThighLift) * groundPoseMask +
      swimLegTuck +
      sitHipFlex
    entry.rightUpperLeg.rotation.z = swimLegSpread
  }
  if (entry.leftLowerLeg) {
    entry.leftLowerLeg.rotation.x =
      (standLowerLeg + leftKneeBend) * groundPoseMask +
      swimKneeBend +
      sitKneeBend
  }
  if (entry.rightLowerLeg) {
    entry.rightLowerLeg.rotation.x =
      (standLowerLeg + rightKneeBend) * groundPoseMask +
      swimKneeBend +
      sitKneeBend
  }
  // 足首ロール（地上走りのみ）: 前方へ振り出した足はかかとから着地（つま先上げ=
  // dorsiflex）、後方では足首を伸ばしつま先で蹴る（plantarflex）。脚の前後位相に同期。
  // 左脚は phase<0 で前方なので (-phase) が前方ふり量、右脚は phase>0 で前方。
  const footRoll =
    runMotionParams.footRoll *
    groundAmp *
    strideBoost *
    groundPoseMask *
    runSwingGate
  if (entry.leftFoot) {
    entry.leftFoot.rotation.x = -phase * footRoll
  }
  if (entry.rightFoot) {
    entry.rightFoot.rotation.x = phase * footRoll
  }
  // 呼吸: 首を曲げて頭を上げ、前を向く（胸上げと合わせて口が水面上に出る）。
  // 呼吸以外の局面では 0 に戻り、顔は水中で視線は斜め下のまま。
  const breathLook = breastBreath * breastGate
  if (entry.neck) {
    entry.neck.rotation.x = BREAST_BREATH_NECK_RAD * breathLook
  }
  if (entry.head) {
    entry.head.rotation.x = BREAST_BREATH_HEAD_RAD * breathLook
  }
  // 遊泳中は拳を開き、指を全て伸ばして水を掻く。陸上では握り拳へ戻る。
  const fistAmount = 1 - swimAmp
  for (const joint of entry.fingerJoints) {
    joint.bone.rotation.y = joint.restY * fistAmount
    joint.bone.rotation.z = joint.restZ * fistAmount
  }

  if (entry.vrm) {
    entry.vrm.update(dt)
  }
}
